import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';

interface StepEnhanceProps {
   isEnhancing: boolean;
}

const StepEnhance: React.FC<StepEnhanceProps> = ({ isEnhancing }) => {
   const [progress, setProgress] = useState(0);
   const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

   useEffect(() => {
      if (isEnhancing) {
         setProgress(0);
         intervalRef.current = setInterval(() => {
            setProgress((prev) => {
               if (prev >= 90) {
                  if (intervalRef.current) clearInterval(intervalRef.current);
                  return 90;
               }
               return prev + Math.random() * 8 + 2;
            });
         }, 300);
      } else {
         setProgress(100);
         if (intervalRef.current) clearInterval(intervalRef.current);
      }

      return () => {
         if (intervalRef.current) clearInterval(intervalRef.current);
      };
   }, [isEnhancing]);

   const statusMessages = [
      'Adjusting brightness and exposure...',
      'Balancing color temperature...',
      'Enhancing contrast and shadows...',
      'Sharpening details...',
      'Applying final adjustments...',
   ];
   const messageIndex = Math.min(Math.floor(progress / 20), statusMessages.length - 1);

   return (
      <Box sx={{
         py: 6,
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center',
         gap: 3,
      }}>
         <Box sx={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(29,158,117,0.12)',
            border: '2px solid #1D9E75',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': {
               '0%, 100%': { boxShadow: '0 0 0 0 rgba(29,158,117,0.3)' },
               '50%': { boxShadow: '0 0 0 12px rgba(29,158,117,0)' },
            },
         }}>
            <Typography sx={{ fontSize: '24px' }}>
               {isEnhancing ? '\u2728' : '\u2705'}
            </Typography>
         </Box>

         <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '16px' }}>
            {isEnhancing ? 'Enhancing your photo...' : 'Enhancement complete!'}
         </Typography>

         <Typography sx={{ color: '#B0BEC5', fontSize: '13px', textAlign: 'center' }}>
            {isEnhancing ? statusMessages[messageIndex] : 'Your enhanced photo is ready'}
         </Typography>

         <Box sx={{ width: '100%', maxWidth: 320 }}>
            <LinearProgress
               variant="determinate"
               value={Math.min(progress, 100)}
               sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: '#253530',
                  '& .MuiLinearProgress-bar': {
                     backgroundColor: '#1D9E75',
                     borderRadius: 3,
                     transition: 'transform 0.3s ease',
                  },
               }}
            />
            <Typography sx={{
               textAlign: 'center', mt: 1,
               fontSize: '12px', color: '#546E7A',
            }}>
               {Math.round(Math.min(progress, 100))}%
            </Typography>
         </Box>
      </Box>
   );
};

export default StepEnhance;
